import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { formatEasternClock, formatEasternDateLine } from '../../utils/eastern-week';
import { formatTourHomeLabel, type BookedTour } from '../../utils/tour-home';

const NOTE_LIMIT = 2000;

interface CancelTourDialogProps {
  tour: BookedTour;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (note: string) => void;
}

export default function CancelTourDialog({
  tour,
  busy,
  error,
  onClose,
  onConfirm,
}: CancelTourDialogProps): ReactElement {
  const titleId = useId();
  const noteId = useId();
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [note, setNote] = useState('');
  const trimmed = note.trim();
  const home = formatTourHomeLabel(tour.unitNumber, tour.propertyName, tour.building);

  useEffect(() => {
    noteRef.current?.focus();
  }, [tour.bookingId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <button
        type="button"
        aria-label="Keep this tour"
        disabled={busy}
        className="absolute inset-0 bg-gray-900/40"
        onClick={onClose}
      />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 p-5 shadow-bento sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed.length === 0 || busy) return;
          onConfirm(trimmed);
        }}
      >
        <h2 id={titleId} className="text-lg font-bold text-gray-900 dark:text-white">
          Cancel this tour
        </h2>
        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
          {home}
          <span className="text-gray-400 dark:text-gray-500"> · </span>
          {formatEasternDateLine(tour.startAt)}
          <span className="text-gray-400 dark:text-gray-500"> · </span>
          {formatEasternClock(tour.startAt, true)}
        </p>
        <label htmlFor={noteId} className="mt-4 block text-sm font-medium text-gray-900 dark:text-white">
          Why are you canceling this tour?
        </label>
        <textarea
          id={noteId}
          ref={noteRef}
          required
          rows={4}
          maxLength={NOTE_LIMIT}
          value={note}
          disabled={busy}
          placeholder="Tell us what changed."
          onChange={(event) => setNote(event.target.value)}
          className="mt-2 w-full resize-y rounded-lg border border-gray-300 dark:border-white/15 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/20 disabled:opacity-60"
        />
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">A note is required before this tour can be canceled.</p>
        {error ? (
          <p role="alert" className="mt-3 rounded-lg border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-sm text-rose-800">
            {error}
          </p>
        ) : null}
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-lg border border-gray-300 dark:border-white/15 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-60"
          >
            Keep this tour
          </button>
          <button
            type="submit"
            disabled={busy || trimmed.length === 0}
            className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? 'Cancelling…' : 'Cancel tour'}
          </button>
        </div>
      </form>
    </div>
  );
}
